import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Chip, IconButton, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const REASON_META = {
  cancelled: {
    label: 'Cancelled',
    color: '#FF1744',
    bg: 'rgba(255,23,68,0.18)',
    description: 'Flight cancelled — defaulted to Reroutes. Edit the tile or driver assignment if needed.',
  },
  unknown_gate: {
    label: 'No gate',
    color: '#FFD600',
    bg: 'rgba(255,214,0,0.18)',
    description: 'Couldn’t parse the outbound gate. Re-paste the TDR or assign manually from the unassigned column.',
  },
  no_route: {
    label: 'Not assigned',
    color: '#FF9100',
    bg: 'rgba(255,145,0,0.18)',
    description: 'Solver couldn’t fit this tile into any route under the current constraints.',
  },
};

function ReviewPopup({ open, onClose, items }) {
  const list = items || [];
  if (list.length === 0) return null;

  // Group by reason for the explanation header
  const reasons = Array.from(new Set(list.map((i) => i.reason)));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(31, 41, 55, 0.97)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 2,
          color: '#fff',
        },
      }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        pr: 1, py: 1.5,
      }}>
        <WarningAmberIcon sx={{ color: '#F9B612' }} />
        <Box sx={{ flex: 1 }}>
          <Typography component="span" sx={{ color: '#F9B612', fontWeight: 700, fontSize: '1.05rem' }}>
            Manual review needed
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem', mt: 0.25 }}>
            {list.length} tile{list.length === 1 ? '' : 's'} need{list.length === 1 ? 's' : ''} a dispatcher decision before printing.
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.6)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* MUI zeroes padding-top on DialogContent that follows a
          DialogTitle (adjacent-sibling rule) — the !important wins it back
          so the first section doesn't hug the header divider. */}
      <DialogContent sx={{ pb: 2, pt: '20px !important' }}>
        {/* One section per reason: the chip + explanation appear exactly
            once as the section header, with bare flight rows beneath.
            The row's right side is reserved for information that adds
            something (the assigned driver) — never a restating of the
            section label. */}
        <Stack spacing={2}>
          {reasons.map((r) => {
            const meta = REASON_META[r] || { label: r, color: '#fff', bg: 'rgba(255,255,255,0.1)', description: '' };
            const rows = list.filter((i) => i.reason === r);
            return (
              <Box key={r}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <Chip
                    size="small"
                    label={meta.label}
                    sx={{
                      backgroundColor: meta.bg, color: meta.color,
                      fontWeight: 700, fontSize: '0.7rem', mt: 0.25, flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem', lineHeight: 1.5, pt: '3px' }}>
                    {meta.description}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderLeft: `3px solid ${meta.color}`,
                  borderRadius: 1.5,
                  overflow: 'hidden',
                }}>
                  {rows.map((item, idx) => (
                    <Box key={`${item.flightNumber}-${idx}`} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      px: 1.5, py: 1,
                      borderBottom: idx === rows.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.05)',
                    }}>
                      <Typography sx={{ flex: 1, minWidth: 0, color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
                        {item.flightNumber || '—'}
                        {item.destination && (
                          <Box component="span" sx={{ color: '#F9B612', fontWeight: 500, ml: 0.75 }}>
                            → {item.destination}
                          </Box>
                        )}
                      </Typography>
                      {item.driverName && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem', fontWeight: 600 }}>
                          {item.driverName}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: '#F9B612', color: '#000',
            fontWeight: 700, textTransform: 'none', px: 3,
            '&:hover': { backgroundColor: '#e0a410' },
          }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReviewPopup;
